import React from 'react';
import {
  Grid,
  TextField,
  Typography,
  Box,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { useTranslation } from '../../store/languageStore';
import SectionHeader from './SectionHeader';

interface TransportTabProps {
  data: any;
  onChange: (data: any) => void;
}

const transportModes = [
  { value: 'sea', label: 'Sea Transport' },
  { value: 'air', label: 'Air Transport' },
  { value: 'road', label: 'Road Transport' },
  { value: 'rail', label: 'Rail Transport' },
  { value: 'post', label: 'Postal/Courier' },
  { value: 'multimodal', label: 'Multimodal Transport' },
  { value: 'fixed_installation', label: 'Fixed Installation' },
  { value: 'inland_waterway', label: 'Inland Waterway' }
];

const incoterms = [
  { value: 'EXW', label: 'EXW - Ex Works' },
  { value: 'FCA', label: 'FCA - Free Carrier' },
  { value: 'CPT', label: 'CPT - Carriage Paid To' },
  { value: 'CIP', label: 'CIP - Carriage and Insurance Paid' },
  { value: 'DAP', label: 'DAP - Delivered at Place' },
  { value: 'DPU', label: 'DPU - Delivered at Place Unloaded' },
  { value: 'DDP', label: 'DDP - Delivered Duty Paid' },
  { value: 'FAS', label: 'FAS - Free Alongside Ship' },
  { value: 'FOB', label: 'FOB - Free on Board' },
  { value: 'CFR', label: 'CFR - Cost and Freight' },
  { value: 'CIF', label: 'CIF - Cost, Insurance and Freight' }
];

const containerTypes = [
  { value: '20GP', label: "20' General Purpose" },
  { value: '40GP', label: "40' General Purpose" },
  { value: '40HC', label: "40' High Cube" },
  { value: '45HC', label: "45' High Cube" },
  { value: '20RF', label: "20' Reefer" },
  { value: '40RF', label: "40' Reefer" },
  { value: '20OT', label: "20' Open Top" },
  { value: '40OT', label: "40' Open Top" }
];

const TransportTab: React.FC<TransportTabProps> = ({ data, onChange }) => {
  const { t, tString } = useTranslation();

  const handleChange = (field: string, value: any) => {
    onChange({
      ...data,
      [field]: value
    });
  };

  return (
      <Box>
        <SectionHeader
          title={t('customsDeclaration.transport.title') as string}
          subtitle={t('customsDeclaration.transport.subtitle') as string}
          icon={<LocalShippingIcon color="primary" sx={{ fontSize: 32 }} />}
        />

        <Grid container spacing={3}>
          {/* Transport Mode & Incoterms */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Transport Details
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Transport Mode</InputLabel>
              <Select
                value={data.mode || 'sea'}
                label="Transport Mode"
                onChange={(e) => handleChange('mode', e.target.value)}
              >
                {transportModes.map((mode) => (
                  <MenuItem key={mode.value} value={mode.value}>
                    {mode.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Incoterms</InputLabel>
              <Select
                value={data.incoterm || 'FOB'}
                label="Incoterms"
                onChange={(e) => handleChange('incoterm', e.target.value)}
              >
                {incoterms.map((term) => (
                  <MenuItem key={term.value} value={term.value}>
                    {term.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Vessel/Flight Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>
              Vessel/Flight Information
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={data.mode === 'air' ? 'Flight Number' : 'Vessel Name'}
              value={data.vessel_name || ''}
              onChange={(e) => handleChange('vessel_name', e.target.value)}
              placeholder={data.mode === 'air' ? 'e.g., LH456' : 'e.g., MSC MARIA'}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={data.mode === 'air' ? 'AWB Number' : 'BL/BOL Number'}
              value={data.bl_number || ''}
              onChange={(e) => handleChange('bl_number', e.target.value)}
              helperText={data.mode === 'air' ? 'Air Waybill Number' : 'Bill of Lading Number'}
            />
          </Grid>

          {data.mode === 'sea' && (
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="IMO Number"
                  value={data.imo_number || ''}
                  onChange={(e) => handleChange('imo_number', e.target.value)}
                  helperText="International Maritime Organization number"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Container Number(s)"
                  value={data.container_numbers || ''}
                  onChange={(e) => handleChange('container_numbers', e.target.value)}
                  placeholder="Comma-separated if multiple"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Container Type</InputLabel>
                  <Select
                    value={data.container_type || '40HC'}
                    label="Container Type"
                    onChange={(e) => handleChange('container_type', e.target.value)}
                  >
                    {containerTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Number of Containers"
                  type="number"
                  value={data.container_count || 1}
                  onChange={(e) => handleChange('container_count', e.target.value)}
                  inputProps={{ min: 1 }}
                />
              </Grid>
            </>
          )}

          {/* Departure & Arrival */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>
              Departure & Arrival
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Port/Airport of Loading"
              value={data.port_of_loading || ''}
              onChange={(e) => handleChange('port_of_loading', e.target.value)}
              placeholder="e.g., Hamburg (DEHAM)"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Port/Airport of Discharge"
              value={data.port_of_discharge || ''}
              onChange={(e) => handleChange('port_of_discharge', e.target.value)}
              placeholder="e.g., New York (USNYC)"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="datetime-local"
              label="Estimated Departure"
              value={data.etd ? data.etd.substring(0, 16) : ''}
              onChange={(e) => handleChange('etd', e.target.value ? new Date(e.target.value).toISOString() : null)}
              InputLabelProps={{ shrink: true }}
              helperText="Estimated Time of Departure"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="datetime-local"
              label="Estimated Arrival"
              value={data.eta ? data.eta.substring(0, 16) : ''}
              onChange={(e) => handleChange('eta', e.target.value ? new Date(e.target.value).toISOString() : null)}
              InputLabelProps={{ shrink: true }}
              helperText="Estimated Time of Arrival"
            />
          </Grid>

          {/* Location Details */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>
              Additional Location Details
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Place of Loading"
              value={data.place_of_loading || ''}
              onChange={(e) => handleChange('place_of_loading', e.target.value)}
              helperText="Warehouse/factory address"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Final Destination"
              value={data.final_destination || ''}
              onChange={(e) => handleChange('final_destination', e.target.value)}
              helperText="Ultimate consignee location"
            />
          </Grid>

          {/* Shipping Details */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>
              Shipping Measurements
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Total Gross Weight (kg)"
              type="number"
              value={data.total_gross_weight || ''}
              onChange={(e) => handleChange('total_gross_weight', e.target.value)}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Total Net Weight (kg)"
              type="number"
              value={data.total_net_weight || ''}
              onChange={(e) => handleChange('total_net_weight', e.target.value)}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Volume (m³)"
              type="number"
              value={data.volume || ''}
              onChange={(e) => handleChange('volume', e.target.value)}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>

          {/* Additional Information */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Transport Remarks"
              value={data.remarks || ''}
              onChange={(e) => handleChange('remarks', e.target.value)}
              placeholder="Any special transport instructions or notes"
            />
          </Grid>
        </Grid>
      </Box>
  );
};

export default TransportTab;
